import { memo } from 'react'
import { Box, Typography } from '@mui/material'
import { indigo, pink } from '@mui/material/colors'
import { DocumentChip } from '@/types/documents'
import { ProjectGridField } from '@/types/fields'
import { getChipsGroupFrameBounds } from '@/utils/image-zoom-pan'

const BORDER_COLOR = indigo[500]
const SELECTED_FIELD_COLOR = pink['A700']

type ChipFrameProps = {
  chips: DocumentChip[]
  documentPageWidth: number
  documentPageHeight: number
  fieldsMap: Record<string, ProjectGridField>
  isForSelectedField: boolean
  labelFontSize: number
  onFieldClick: (fieldId: string, rowId: string) => void
}

const ChipFrame = ({
  chips,
  documentPageWidth,
  documentPageHeight,
  fieldsMap,
  isForSelectedField,
  labelFontSize,
  onFieldClick,
}: ChipFrameProps) => {
  const { x, y, width, height } = getChipsGroupFrameBounds(
    chips,
    documentPageWidth,
    documentPageHeight,
  )
  const fieldId = chips[0].project_grid_field_id as string
  const rowId = chips[0].document_row_id as string
  const fieldName = fieldsMap[fieldId]?.name
  const color = isForSelectedField ? SELECTED_FIELD_COLOR : BORDER_COLOR

  return (
    <Box
      key={chips[0].id}
      onMouseDownCapture={(e) => {
        e.stopPropagation()
        e.preventDefault()
      }}
      sx={{
        zIndex: isForSelectedField ? 2 : 1,
        position: 'absolute',
        top: y,
        left: x,
        width,
        height,
        pointerEvents: 'auto',
        cursor: 'pointer',
        transition: 'background-color 0.05s ease-in-out',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          zIndex: 3,
        },
      }}
    >
      {/* Label */}
      <Typography
        onClick={() => {
          onFieldClick(fieldId, rowId)
        }}
        sx={{
          color: 'white',
          position: 'absolute',
          top: 0,
          left: 0,
          transform: 'translate(0, -100%)',
          width: 'fit-content',
          border: `1px solid ${color}`,
          backgroundColor: color,
          transition: 'background-color 0.05s ease-in-out',
          fontSize: labelFontSize,
          lineHeight: `${labelFontSize - 1}px`,
          borderRadius: '1px 0 0 1px',
          pt: '1px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          boxShadow: 1,
        }}
      >
        {fieldName}
      </Typography>
      {/* Highlight box */}
      <Box
        tabIndex={0}
        onClick={() => {
          onFieldClick(fieldId, rowId)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onFieldClick(fieldId, rowId)
          }
        }}
        sx={{
          height: '100%',
          width: '100%',
          transition: 'all 0.05s ease-in-out',
          border: `1px solid ${color}`,
        }}
      />
    </Box>
  )
}

// Add this equality check for better memoization
export default memo(ChipFrame, (prevProps, nextProps) => {
  // Only re-render if these props change
  return (
    prevProps.chips[0].id === nextProps.chips[0].id &&
    prevProps.isForSelectedField === nextProps.isForSelectedField &&
    prevProps.labelFontSize === nextProps.labelFontSize &&
    prevProps.documentPageWidth === nextProps.documentPageWidth &&
    prevProps.documentPageHeight === nextProps.documentPageHeight
  )
})
